import React, { useEffect, useRef, useState } from 'react';
import Spinner from '../../../components/Spinner/Spinner';
import ReelFeedItem from '../../../components/Reels/ReelFeedItem/ReelFeedItem';
import { EmptyState, FeedScroller, LoaderContainer } from '../ReelsPage.styles';

const PRELOAD_REELS_BEFORE = 1;
const PRELOAD_REELS_AFTER = 2;

const ReelsFeedList = ({
  reels,
  emptyStateMessage,
  onAvatarClick,
  onLikeToggle,
  onOpenComments,
  onOpenShare,
  targetReelId,
  onTargetReelLocated,
  onActiveReelChange,
  hasMore,
  loadMoreRef,
  fetchMorePending,
  reelOverlayBottomPadding,
  reelOverlayZIndex,
  showItemGradients = true,
  showAvatarButton = true,
}) => {
  const scrollerRef = useRef(null);
  const reelRefs = useRef(new Map());
  const [activeReelId, setActiveReelId] = useState(reels[0]?.id || null);
  const activeReelIndex = reels.findIndex(reel => reel.id === activeReelId);

  useEffect(() => {
    if (!reels.length) {
      setActiveReelId(null);
      return;
    }

    setActiveReelId(currentActiveId => {
      if (currentActiveId && reels.some(reel => reel.id === currentActiveId)) {
        return currentActiveId;
      }

      return reels[0].id;
    });
  }, [reels]);

  useEffect(() => {
    onActiveReelChange?.(activeReelId);
  }, [activeReelId, onActiveReelChange]);

  useEffect(() => {
    const scroller = scrollerRef.current;

    if (!scroller || !reels.length) {
      return undefined;
    }

    let frameId = null;

    const updateActiveReel = () => {
      frameId = null;

      const scrollerRect = scroller.getBoundingClientRect();
      const scrollerCenter = scrollerRect.top + scrollerRect.height / 2;
      let nextActiveReelId = reels[0]?.id || null;
      let nearestDistance = Number.POSITIVE_INFINITY;

      reels.forEach(reel => {
        const reelNode = reelRefs.current.get(reel.id);

        if (!reelNode) {
          return;
        }

        const reelRect = reelNode.getBoundingClientRect();
        const reelCenter = reelRect.top + reelRect.height / 2;
        const distanceToCenter = Math.abs(reelCenter - scrollerCenter);

        if (distanceToCenter < nearestDistance) {
          nearestDistance = distanceToCenter;
          nextActiveReelId = reel.id;
        }
      });

      setActiveReelId(currentActiveId => (currentActiveId === nextActiveReelId ? currentActiveId : nextActiveReelId));
    };

    const scheduleActiveReelUpdate = () => {
      if (frameId !== null) {
        return;
      }

      frameId = window.requestAnimationFrame(updateActiveReel);
    };

    scheduleActiveReelUpdate();
    scroller.addEventListener('scroll', scheduleActiveReelUpdate, { passive: true });
    window.addEventListener('resize', scheduleActiveReelUpdate);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      scroller.removeEventListener('scroll', scheduleActiveReelUpdate);
      window.removeEventListener('resize', scheduleActiveReelUpdate);
    };
  }, [reels]);

  useEffect(() => {
    if (!targetReelId) {
      return;
    }

    const targetNode = reelRefs.current.get(targetReelId);

    if (!targetNode) {
      return;
    }

    targetNode.scrollIntoView({ block: 'start', behavior: 'auto' });
    setActiveReelId(targetReelId);
    onTargetReelLocated?.();
  }, [onTargetReelLocated, reels, targetReelId]);

  const setReelRef = (reelId, isLoadMoreTarget) => node => {
    if (node) {
      reelRefs.current.set(reelId, node);
    } else {
      reelRefs.current.delete(reelId);
    }

    if (isLoadMoreTarget) {
      loadMoreRef.current = node;
    }
  };

  return (
    <FeedScroller ref={scrollerRef}>
      {reels.length ? (
        reels.map((reel, index) => (
          <ReelFeedItem
            key={reel.id}
            ref={setReelRef(reel.id, hasMore && index === reels.length - 1)}
            reel={reel}
            isActive={activeReelId === reel.id}
            shouldLoadVideo={
              activeReelIndex === -1 ||
              (index >= activeReelIndex - PRELOAD_REELS_BEFORE && index <= activeReelIndex + PRELOAD_REELS_AFTER)
            }
            liked={reel.liked}
            likesCount={reel.stats.likesCount}
            likeDisabled={reel.likePending}
            commentsCount={reel.stats.commentsCount}
            sharesCount={reel.stats.sharesCount}
            onAvatarClick={() => onAvatarClick(reel)}
            onLikeToggle={() => onLikeToggle(reel)}
            onOpenComments={() => onOpenComments(reel)}
            onOpenShare={() => onOpenShare(reel)}
            overlayBottomPadding={reelOverlayBottomPadding}
            overlayZIndex={reelOverlayZIndex}
            showGradients={showItemGradients}
            showAvatarButton={showAvatarButton}
          />
        ))
      ) : (
        <EmptyState>{emptyStateMessage}</EmptyState>
      )}
      {fetchMorePending ? (
        <LoaderContainer>
          <Spinner width={90} height={90} />
        </LoaderContainer>
      ) : null}
    </FeedScroller>
  );
};

export default ReelsFeedList;
